/**
 * Subscription Selection Step
 *
 * First step in subscription purchase flow - plan selection.
 * Displays tabs for Memberships and Passes with grid of available plans.
 *
 * **Features:**
 * - Tabs for switching between Memberships and Passes
 * - Grid of subscription plan cards
 * - Clicking a card immediately navigates to review step
 * - Loading and error states
 *
 * **Design:**
 * - Mobile-first responsive grid
 * - W3C APG compliant tab navigation
 * - Follows ButterUI conventions
 *
 * @module pages/subscription/steps
 */

import { useRef, useEffect } from "react";
import { MembershipCard, PassCard } from "@butter/ui/domains/subscriptions";
import { ArrowIcon } from "@butter/ui/icons";
import { useTabNavigation } from "~/hooks/useTabNavigation";
import {
  useSubscriptionPurchase,
  useSubscriptionStep,
  type SubscriptionTab,
} from "~/services/internalApi/subscription";
import { useAvailableSubscriptions } from "~/services/internalApi/subscription/hooks/useAvailableSubscriptions";
import { useClubAccess, CLUB_NO_ACCESS_LABEL, CLUB_NO_ACCESS_MESSAGE } from "~/services/internalApi/club";
import { QueryParams } from "~/lib/queryParams";
import { useTenantSearchParams } from "~/lib/navigation";
import { formatMembershipPlan, formatPassPlan } from '~/services/internalApi/subscription/utils/formatPlan';
import { PromoNotice } from "./QuoteBanners";
import { createCompositeId } from "~/lib/utils/subscriptionIdUtils";
import type { Membership } from "@lgc/database/types/domains/membership";
import type { Pass } from "@lgc/database/types/domains/pass";

/**
 * Subscription Selection Step
 *
 * Main selection page with tabs and grid layout.
 */
interface SubscriptionSelectionStepProps {
  /**
   * True while a `?plan=` deep link is still resolving. Owned by the flow page,
   * which is where `usePreselectedPlan` is mounted.
   */
  isResolvingPreselectedPlan?: boolean
}

export function SubscriptionSelectionStep({
  isResolvingPreselectedPlan = false,
}: SubscriptionSelectionStepProps) {
  const {
    selectedSubscription,
    setSelectedSubscription,
    selectedTab,
    setSelectedTab,
    promoCode,
  } = useSubscriptionPurchase();

  const { goToNextStep } = useSubscriptionStep();

  // Block purchase when an authenticated member is viewing a club they aren't
  // enrolled in — subscriptions are bought at the member's own club, not here.
  const { status: clubAccessStatus } = useClubAccess();

  const [, setParams] = useTenantSearchParams();

  // Ref flag: when true, the next re-render with a non-null selectedSubscription
  // will trigger navigation. This avoids bypassing step-context validation.
  const shouldAdvance = useRef(false);

  // Fetch available subscriptions
  const { memberships, passes, isLoading: listLoading, error } = useAvailableSubscriptions();
  // Hold the skeleton while a deep link is still resolving, so an unlisted plan
  // does not flash the empty grid on its way to Review.
  const isLoading = listLoading || isResolvingPreselectedPlan;

  // After React re-renders with the newly selected subscription, advance to the
  // next step. The ref guard ensures we only auto-advance when the user just
  // clicked a plan (not on mount or other re-renders).
  useEffect(() => {
    if (shouldAdvance.current && selectedSubscription) {
      shouldAdvance.current = false;
      goToNextStep();
    }
  }, [selectedSubscription, goToNextStep]);

  // Tab navigation
  const tabs: readonly SubscriptionTab[] = ["memberships", "passes"] as const;
  const { tabListProps, getTabProps, getTabPanelProps } = useTabNavigation({
    activeTab: selectedTab,
    onTabChange: setSelectedTab,
    tabs,
    orientation: "horizontal",
  });

  // Handle membership card click - format and select, including usage constraints
  const handleMembershipClick = (membership: Membership) => {
    // Update URL so the plan is shareable (replaces the history entry by default)
    setParams({ [QueryParams.PLAN]: createCompositeId('membership', membership.id as number) });
    setSelectedSubscription(formatMembershipPlan(membership));
    shouldAdvance.current = true;
  };

  // Handle pass card click - format and select, including usage constraints
  const handlePassClick = (pass: Pass) => {
    // Update URL so the plan is shareable
    setParams({ [QueryParams.PLAN]: createCompositeId('pass', pass.id as number) });
    setSelectedSubscription(formatPassPlan(pass));
    shouldAdvance.current = true;
  };

  // Not enrolled here → no purchase. Mirror the booking CTA's "No access" gate.
  if (clubAccessStatus === 'no-access') {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center" data-testid="subscription-no-access">
        <p className="text-base font-medium text-fg-primary">{CLUB_NO_ACCESS_LABEL}</p>
        <p className="mt-2 text-sm text-fg-quaternary">{CLUB_NO_ACCESS_MESSAGE}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Promo capture acknowledgement (7a): the member clicked a promo link —
          say so before they pick a plan. Validity is only known once a pass is
          quoted, so this speaks to intent ("will be applied"), and the Review
          step's notice takes over with the server's verdict. */}
      {promoCode && (
        <div className="mb-6">
          <PromoNotice variant="applied" testId="promo-applied-chip">
            Promo code <span className="font-semibold">{promoCode}</span> will be applied at
            checkout.
          </PromoNotice>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8">
        <div
          {...tabListProps}
          className="inline-flex rounded-full bg-gray-100 p-1"
        >
          <button
            {...getTabProps("memberships")}
            className="
              rounded-full px-6 py-2 text-sm font-medium transition-colors
              aria-selected:bg-white aria-selected:text-gray-900 aria-selected:shadow-sm
              text-gray-600 hover:text-gray-900
            "
            data-testid="subscription-tab-memberships"
          >
            Memberships
          </button>
          <button
            {...getTabProps("passes")}
            className="
              rounded-full px-6 py-2 text-sm font-medium transition-colors
              aria-selected:bg-white aria-selected:text-gray-900 aria-selected:shadow-sm
              text-gray-600 hover:text-gray-900
            "
            data-testid="subscription-tab-passes"
          >
            Passes
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div role="status" aria-live="polite" className="py-12">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-80 animate-pulse rounded-2xl bg-gray-200"
                aria-hidden="true"
                data-testid={`subscription-loading-card-${i}`}
              />
            ))}
          </div>
          <span className="sr-only">Loading subscription plans...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-800" data-testid="subscription-error-message">
            Failed to load subscription plans. Please try again.
          </p>
        </div>
      )}

      {/* Memberships Tab Panel */}
      {!isLoading && !error && (
        <div {...getTabPanelProps("memberships")}>
          <h2 className="mb-6 text-xl font-semibold text-gray-900" data-testid="subscription-memberships-header">
            Available memberships
          </h2>

          {memberships.length === 0 ? (
            <div className="py-12 text-center" data-testid="subscription-no-memberships">
              <p className="text-gray-600">
                No memberships available at this time.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {memberships.map((membership) => (
                <MembershipCard
                  key={`membership-${membership.id}`}
                  membership={membership}
                  onClick={handleMembershipClick}
                  overlayLabel={`Select ${membership.name}`}
                  opensDialog={false}
                  data-testid={`subscription-membership-card-${membership.id}`}
                  action={
                    <button
                      type="button"
                      onClick={() => handleMembershipClick(membership)}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-theme-600 text-white transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2"
                      aria-label={`Select ${membership.name}`}
                    >
                      <ArrowIcon className="h-5 w-5 -rotate-45" />
                    </button>
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Passes Tab Panel */}
      {!isLoading && !error && (
        <div {...getTabPanelProps("passes")}>
          <h2 className="mb-6 text-xl font-semibold text-gray-900" data-testid="subscription-passes-header">
            Available passes
          </h2>

          {passes.length === 0 ? (
            <div className="py-12 text-center" data-testid="subscription-no-passes">
              <p className="text-gray-600">No passes available at this time.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {passes.map((pass) => (
                <PassCard
                  key={`pass-${pass.id}`}
                  pass={pass}
                  onClick={handlePassClick}
                  overlayLabel={`Select ${pass.name}`}
                  opensDialog={false}
                  data-testid={`subscription-pass-card-${pass.id}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
